import React, { useMemo, useState } from 'react';
import { List as ImmutableList } from 'immutable';
import useRootSelector from 'common/hooks/useRootSelector';
import { FormattedMessage } from 'react-intl';
import { Button, makeStyles, Menu, MenuItem, Theme, Typography } from '@material-ui/core';
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import * as actions from 'modules/authentication/state/actions';
import { resetSelectedEntranceId } from 'modules/front-desk/state/actions';
import * as selectors from 'modules/authentication/state/selectors';
import { useAppDispatch } from 'store/hooks';
import useComponentUpdate from 'common/hooks/useComponentUpdate';
import { IUserOrganization, IUserOrganizationImt } from 'common/interfaces/clients';
import { IUserProfileInfoImt } from 'modules/authentication/interfaces';
import { HeaderId } from 'common/enums/html.enums';
import messages from 'common/messages/messages';
import { PermissionLevels } from 'common/constants/permissions';
import { useLocation, useNavigate } from 'react-router-dom';
import { renderTimezoneOption } from 'modules/corporate-settings/utils/timezoneUtils';
import { colors } from 'common/ui/theme/default';

const useStyles = makeStyles((theme: Theme) => ({
  organizationOption: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
  },
  organizationTitle: {
    textTransform: 'none',
    fontWeight: 600,
  },
  dropDownIcon: {
    marginLeft: theme.spacing(1),
  },
  timeZone: {
    textTransform: 'none',
  },
}));

function ClubSelector(): JSX.Element {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const currentUser: IUserProfileInfoImt = useRootSelector(selectors.selectCurrentUser);
  const selectedOrg: IUserOrganizationImt = useRootSelector(
    selectors.selectUserSelectedOrganization,
  );
  const organizations: ImmutableList<IUserOrganizationImt> = useRootSelector(
    selectors.selectCurrentUserOrganizationsByRoles,
  );

  useComponentUpdate();

  const hasMultipleOrganizations = useMemo(() => !!organizations?.size, [organizations]);

  const onMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (hasMultipleOrganizations) setAnchorEl(event.currentTarget);
  };

  const onMenuClose = () => {
    setAnchorEl(null);
  };

  const handleChangeSelectedOrg = (organization: IUserOrganization): void => {
    dispatch(
      actions.selectCurrentUserOrganization(currentUser.get('id'), {
        ...organization,
        color:
          organization.color && organization.color !== 'None'
            ? organization.color
            : colors.colorPicker.defaultColor,
      }),
    );
    dispatch(resetSelectedEntranceId());

    if (
      (location.pathname.includes('club-settings') &&
        organization.permissionsLevel === PermissionLevels.CORPORATE) ||
      (location.pathname.includes('corporate-settings') &&
        organization.permissionsLevel === PermissionLevels.CLIENT)
    ) {
      navigate('/');
    }
  };

  return (
    <>
      <Button id={HeaderId.HeaderBtnClub} onClick={onMenuOpen}>
        <Typography component="span" className={classes.organizationOption}>
          <Typography component="span" color="secondary" className={classes.organizationTitle}>
            {selectedOrg?.get('title') || <FormattedMessage {...messages.chooseClubTitle} />}
          </Typography>

          <Typography
            component="span"
            variant="body2"
            color="secondary"
            className={classes.timeZone}
          >
            {renderTimezoneOption(selectedOrg?.get('timezone'))}
          </Typography>
        </Typography>

        {hasMultipleOrganizations && anchorEl ? (
          <ArrowDropUp color="secondary" className={classes.dropDownIcon} />
        ) : (
          <ArrowDropDown color="secondary" className={classes.dropDownIcon} />
        )}
      </Button>

      <Menu
        id="actions-menu"
        keepMounted
        getContentAnchorEl={null}
        title={selectedOrg?.get('title')}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        {organizations?.map(organization => (
          <MenuItem
            key={organization?.get('id')}
            value={organization?.get('id')}
            onClick={() => {
              onMenuClose();
              handleChangeSelectedOrg(organization.toJS());
            }}
          >
            <Typography className={classes.organizationOption}>
              <Typography component="span" color="secondary" className={classes.organizationTitle}>
                {organization?.get('title')}
              </Typography>

              <Typography component="span" variant="body2" color="secondary">
                {renderTimezoneOption(organization?.get('timezone'))}
              </Typography>
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default ClubSelector;
